import React, { useState, useEffect} from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import { EXTERNAL } from 'routes';
import { FormProvider, TextField, Select } from 'components/Form';
import { Modal } from 'components/Modal';

import { useCompanyUsersCount } from '../../companies/api/getCompanyUsersCount';
import { useSubscribe } from '../../subscriptions/api/subscribe';

import useYupValidationResolver from 'hook/useYupValidationResolver';
import { subscriptionSchema } from '../../subscriptions/validation';

import { useAuth } from 'lib/auth';

const getDefaultValues = (currentLevel, currentPeriod, usersCount) => ({
  subscription_level: 'gold',
  invoice_period: currentPeriod,
  number_of_users: usersCount,
  promo_code: '',
});

const subscriptionLevelsSuppliers = [
  {
    label: 'Free',
    value: 'free',
  },
  /*{
    label: 'Silver 395 SEK / Month / user',
    value: 'silver',
  },*/
  {
    label: 'Plus 795 SEK / Month / user',
    value: 'gold',
  },
];

const subscriptionLevelsBuyers = [
  {
    label: 'Free',
    value: 'free',
  },
  /*{
    label: 'Silver 1.995 SEK / Month / user',
    value: 'silver',
  },*/
  {
    label: 'Plus 3.995 SEK / Month / user',
    value: 'gold',
  },
];

const invoicePeriodsSuppliers = [
  {
    label: 'Quaterly',
    value: 'quaterly',
  },
  {
    label: 'Annual',
    value: 'annual',
  },
];

const invoicePeriodsBuyers = [
  {
    label: 'Quaterly',
    value: 'quaterly',
  },
  {
    label: 'Annual (-20%)',
    value: 'annual',
  },
];

export const SuperAdminSubscribeForm = ({handleClose, handleSuccess, open, currentLevel, currentInvoicePeriod,  company = null }) => {
  const { user } = useAuth();
  const [ numberOfUsers, setNumberOfUsers ] = useState('');
  const [ subscriptionLevel, setSubscriptionLevel ] = useState(currentLevel);
  const [ invoicePeriod, setInvoicePeriod ] = useState(currentInvoicePeriod);
  const [ disabled, setDisabled ] = useState(true);
  const companyID = company?.company_id ? company.company_id : user.Company.company_id;
  const subscriptionLevels = company.Type === 'buyer' ? subscriptionLevelsBuyers : subscriptionLevelsSuppliers;
  const invoicePeriods = company.Type === 'buyer' ? invoicePeriodsBuyers : invoicePeriodsSuppliers;
  
  const {
    //@ts-ignore
    data: { count: usersCount },
  } = useCompanyUsersCount(companyID);  

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: getDefaultValues(currentLevel, invoicePeriod, usersCount),
    resolver: useYupValidationResolver(subscriptionSchema),
  });

  const onSubmit = async (formData) => {
    if (disabled) {
      return;
    }

    if (formData.subscription_level !== 'free' && formData.number_of_users < usersCount) {
      methods.setError('users', { type: 'custom', message: `Can not be lower than number of active users (currently ${usersCount})` });
    } else {
      try {
        subscribe({...formData}, { onSuccess });
      } catch (error) {
        methods.setError('number_of_users', { type: 'custom', message: error.message });
      }
    }
  };  

  const handleNumberOfUsersChange = (value) => {
    if (value !== usersCount) {
      setDisabled(false);
    }

    setNumberOfUsers(value);
    methods.setValue("number_of_users", value);
  }

  const handleSubscriptionLevelChange = (e) => {
    if (e.target.value !== currentLevel) {
      setDisabled(false);
    }

    setSubscriptionLevel(e.target.value);
    methods.setValue("subscription_level", e.target.value);
  }

  const handleInvoicePeriodChange = (e) => {
    if (e.target.value !== currentLevel) {
      setDisabled(false);
    }

    setInvoicePeriod(e.target.value);
    methods.setValue("invoice_period", e.target.value);
  }

  const { mutate: subscribe } = useSubscribe(+companyID, +user.user_id);

  const onClose = () => {
    methods.reset({ ...getDefaultValues(currentLevel, invoicePeriod, usersCount) });
    setNumberOfUsers(usersCount);
    setSubscriptionLevel(currentLevel);
    setInvoicePeriod(invoicePeriod);
    setDisabled(true);
    handleClose();
  };

  const onSuccess = () => {
    //handleSuccess();
    refreshPage();
    //onClose();
  };

  const refreshPage = () => {
    window.location.reload();
  }

  useEffect(() => {
    handleNumberOfUsersChange(usersCount)
  }, [companyID])  
  
  return (
    <Modal title="Current subscription level" handleClose={onClose} open={open} gap={0}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack>
        <Stack>
          <Typography variant="h6" sx={{fontWeight: 500, textTransform: 'capitalize', mb: 3 }}>
            {currentLevel}
          </Typography>
          <Select value={subscriptionLevel} onChange={handleSubscriptionLevelChange} placeholder="Please select" required fullWidth name="subscription_level" label="New Subscription level" options={subscriptionLevels} sx={{ mb: 2 }} />
          {subscriptionLevel !== "free" && (
            <Select value={invoicePeriod} onChange={handleInvoicePeriodChange} placeholder="Please select" required fullWidth name="invoice_period" label="Invoice period" options={invoicePeriods} sx={{ mb: 2 }} />
          )}
          </Stack>
        
        <Stack>
          {subscriptionLevel !== "free" && (
            <TextField
              name="number_of_users"
              label="Number of users"
              placeholder="Please enter number of users"
              required={true}
              type="text"
              value={numberOfUsers}
              onChange = {(e) => { handleNumberOfUsersChange(e.target.value); }}
              sx={{ mb: 2 }}
              fullWidth
              error={Boolean(methods?.formState?.errors?.users)}
              helperText={methods?.formState?.errors?.users?.message.toString()}
            />
          )}
          <Button
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 2, mb: 0.5 }}
            disabled = {disabled}
          >
            Change subscription
          </Button>
        </Stack>
      </Stack>
      </FormProvider>
    </Modal>
  );
};
